import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  EyeOutlined,
  ForkOutlined,
  PlayCircleOutlined,
  PlusOutlined,
  UserAddOutlined,
} from "@ant-design/icons";
import { PropTypes } from "@scalableminds/prop-types";
import { useQueryClient } from "@tanstack/react-query";
import AdminPage from "admin/admin_page";
import AnnotationServicesAd from "admin/ads/annotation_services_alert";
import {
  assignTaskToUser as assignTaskToUserAPI,
  deleteTask as deleteTaskAPI,
  getTasks,
} from "admin/api/tasks";
import { downloadAnnotation as downloadAnnotationAPI } from "admin/rest_api";
import TaskAnnotationView from "admin/task/task_annotation_view";
import { downloadTasksAsCSV } from "admin/task/task_create_form_view";
import type { QueryObject, TaskFormFieldValues } from "admin/task/task_search_form";
import TaskSearchForm from "admin/task/task_search_form";
import UserSelectionComponent from "admin/user/user_selection_component";
import { Alert, App, Button, Input, Modal, Spin, Tag } from "antd";
import type { ColumnType } from "antd/es/table/interface";
import { AsyncLink } from "components/async_clickables";
import FixedExpandableTable from "components/fixed_expandable_table";
import FormattedDate from "components/formatted_date";
import FormattedDuration from "components/formatted_duration";
import FormattedId from "components/formatted_id";
import LinkButton from "components/link_button";
import features from "features";
import { copyToClipboard } from "libs/clipboard";
import { handleGenericError } from "libs/error_handling";
import { formatTuple } from "libs/format_utils";
import Persistence from "libs/persistence";
import { useQueryWithErrorHandling } from "libs/react_hooks";
import Toast from "libs/toast";
import {
  compareBy,
  filterWithSearchQueryAND,
  getUrlParamValue,
  hasUrlParam,
  localeCompareBy,
  scrollToTop,
} from "libs/utils";
import isEmpty from "lodash-es/isEmpty";
import partial from "lodash-es/partial";
import messages from "messages";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router";
import type { APITask, APITaskType, TaskStatus } from "types/api_types";

const { Search, TextArea } = Input;

type Props = {
  initialFieldValues?: TaskFormFieldValues;
};

const persistence = new Persistence<{ searchQuery: string }>(
  {
    searchQuery: PropTypes.string,
  },
  "taskList",
);

function getTasksQueryOptions(queryObject: QueryObject) {
  return {
    queryKey: ["tasks", queryObject],
    queryFn: () => getTasks(queryObject),
    // Task lists can be large and the search can be random, so don't persist them and
    // drop the results of earlier searches after a while.
    gcTime: 5 * 60 * 1000,
    meta: { persist: false },
  };
}

function TaskListView({ initialFieldValues }: Props) {
  const { modal } = App.useApp();
  const { taskId, projectId, taskTypeId } = useParams();

  initialFieldValues = {
    ...initialFieldValues,
    taskId,
    projectId,
    taskTypeId,
  };

  const queryClient = useQueryClient();
  const [queryObject, setQueryObject] = useState<QueryObject>({});
  const [isLoadingMutation, setIsLoadingMutation] = useState(false);
  const [searchQuery, setSearchQuery] = useState(() => persistence.load().searchQuery || "");
  const selectedUserIdForAssignment = useRef<string | null>(null);
  const [isAnonymousTaskLinkModalOpen, setIsAnonymousTaskLinkModalOpen] = useState(
    hasUrlParam("showAnonymousLinks"),
  );

  const tasksQueryOptions = getTasksQueryOptions(queryObject);
  const { data: tasks = [], isFetching: isFetchingTasks } = useQueryWithErrorHandling({
    ...tasksQueryOptions,
    enabled: !isEmpty(queryObject),
    refetchOnWindowFocus: false,
  });
  const isLoading = isFetchingTasks || isLoadingMutation;

  async function setTasks(updater: (currentTasks: APITask[]) => APITask[]) {
    // Cancel a running refetch so that its stale response cannot overwrite this update.
    await queryClient.cancelQueries({ queryKey: tasksQueryOptions.queryKey });
    queryClient.setQueryData(tasksQueryOptions.queryKey, (currentTasks: APITask[] = []) =>
      updater(currentTasks),
    );
  }

  useEffect(() => {
    persistence.persist({ searchQuery });
  }, [searchQuery]);

  // Returns null if the tasks could not be loaded.
  async function fetchData(newQueryObject: QueryObject): Promise<APITask[] | null> {
    setQueryObject(newQueryObject);
    if (isEmpty(newQueryObject)) {
      return [];
    }
    try {
      // Always refetch, even if the query didn't change (e.g., for random tasks or to refresh
      // the list). An in-flight request for the same query is reused.
      return await queryClient.fetchQuery(getTasksQueryOptions(newQueryObject));
    } catch (_error) {
      // The error is shown by useQueryWithErrorHandling.
      return null;
    }
  }

  function handleSearch(event: React.ChangeEvent<HTMLInputElement>): void {
    setSearchQuery(event.target.value);
  }

  function deleteTask(task: APITask) {
    modal.confirm({
      title: messages["task.delete"],
      onOk: async () => {
        try {
          setIsLoadingMutation(true);
          await deleteTaskAPI(task.id);
          setTasks((currentTasks) => currentTasks.filter((t) => t.id !== task.id));
        } catch (error) {
          handleGenericError(error as Error);
        } finally {
          setIsLoadingMutation(false);
        }
      },
    });
  }

  function assignTaskToUser(task: APITask) {
    selectedUserIdForAssignment.current = null;
    modal.confirm({
      title: "Manual Task Assignment",
      icon: <UserAddOutlined />,
      content: (
        <>
          <div>Please, select a user to manually assign this task to:</div>
          <div style={{ marginTop: 10, marginBottom: 25 }}>
            <UserSelectionComponent
              handleSelection={(value) => {
                selectedUserIdForAssignment.current = value;
              }}
            />
          </div>
          <Alert
            title="Note, manual assignments will bypass the automated task distribution system and its checks for user experience, access rights and other eligibility criteria."
            type="info"
          />
        </>
      ),
      onOk: async () => {
        const userId = selectedUserIdForAssignment.current;
        if (userId != null) {
          try {
            const updatedTask = await assignTaskToUserAPI(task.id, userId);

            setTasks((currentTasks) => [
              ...currentTasks.filter((t) => t.id !== task.id),
              updatedTask,
            ]);

            Toast.success("A user was successfully assigned to the task.");
          } catch (error) {
            handleGenericError(error as Error);
          } finally {
            selectedUserIdForAssignment.current = null;
          }
        }
      },
    });
  }

  function getFilteredTasks(tasksToFilter: APITask[] = tasks) {
    return filterWithSearchQueryAND(
      tasksToFilter,
      [
        "team",
        "projectName",
        "id",
        "datasetName",
        "created",
        "type",
        (task) => task.neededExperience.domain,
      ],
      searchQuery,
    );
  }

  async function downloadSettingsFromAllTasks(queryObject: QueryObject) {
    const fetchedTasks = await fetchData(queryObject);
    if (fetchedTasks == null) {
      return;
    }
    const filteredTasks = getFilteredTasks(fetchedTasks);

    if (filteredTasks.length > 0) {
      downloadTasksAsCSV(filteredTasks);
    } else {
      Toast.warning(messages["task.no_tasks_to_download"]);
    }
  }

  function getAnonymousTaskLinkModal() {
    const anonymousTaskId = getUrlParamValue("showAnonymousLinks");

    if (!isAnonymousTaskLinkModalOpen) {
      return null;
    }

    const tasksString = tasks
      .filter((t) => t.id === anonymousTaskId)
      .map((t) => t.directLinks)
      .join("\n");
    return (
      <Modal
        title={`Anonymous Task Links for Task ${anonymousTaskId}`}
        open={isAnonymousTaskLinkModalOpen}
        onOk={() => {
          copyToClipboard(tasksString, "links");
          setIsAnonymousTaskLinkModalOpen(false);
        }}
        onCancel={() => setIsAnonymousTaskLinkModalOpen(false)}
      >
        <TextArea
          autoSize={{
            minRows: 2,
            maxRows: 10,
          }}
          defaultValue={tasksString}
        />
      </Modal>
    );
  }

  function renderPlaceholder() {
    return (
      <>
        <p>
          There are no tasks in the current search. Select search criteria above or create new tasks
          by clicking on the <strong>Add Task</strong> button.
        </p>
        <p>
          To learn more about the task system in WEBKNOSSOS,{" "}
          <a
            href="https://docs.webknossos.org/webknossos/tasks_projects/index.html"
            rel="noopener noreferrer"
            target="_blank"
          >
            check out the documentation
          </a>
          .
        </p>
      </>
    );
  }

  const columns: ColumnType<APITask>[] = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      sorter: localeCompareBy<APITask>((task) => task.id),
      render: (id: string) => <FormattedId id={id} />,
      width: 120,
    },
    {
      title: "Project",
      dataIndex: "projectName",
      key: "projectName",
      width: 130,
      sorter: localeCompareBy<APITask>((task) => task.projectName),
      render: (projectName: string) => <a href={`/projects#${projectName}`}>{projectName}</a>,
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      width: 200,
      sorter: localeCompareBy<APITask>((task) => task.type.summary),
      render: (taskType: APITaskType) => (
        <a href={`/taskTypes#${taskType.id}`}>{taskType.summary}</a>
      ),
    },
    {
      title: "Dataset",
      dataIndex: "datasetName",
      key: "datasetName",
      sorter: localeCompareBy<APITask>((task) => task.datasetName),
    },
    {
      title: "Stats",
      dataIndex: "status",
      key: "status",
      render: (status: APITask["status"], task: APITask) => (
        <div className="nowrap">
          <span title="Pending Instances">
            <PlayCircleOutlined className="icon-margin-right" />
            {status.pending}
          </span>
          <br />
          <span title="Active Instances">
            <ForkOutlined className="icon-margin-right" />
            {status.active}
          </span>
          <br />
          <span title="Finished Instances">
            <CheckCircleOutlined className="icon-margin-right" />
            {status.finished}
          </span>
          <br />
          <span title="Annotation Time">
            <ClockCircleOutlined className="icon-margin-right" />
            <FormattedDuration durationMs={task.tracingTime || 0} />
          </span>
        </div>
      ),
      filters: [
        {
          text: "Has Pending Instances",
          value: "pending",
        },
        {
          text: "Has Active Instances",
          value: "active",
        },
        {
          text: "Has Finished Instances",
          value: "finished",
        },
      ],
      onFilter: (key: any, task: APITask) => task.status[key as unknown as keyof TaskStatus] > 0,
    },
    {
      title: "Edit Position / Bounding Box",
      dataIndex: "editPosition",
      key: "editPosition",
      width: 150,
      render: (_, task: APITask) => (
        <div className="nowrap">
          {formatTuple(task.editPosition)} <br />
          <span>{formatTuple(task.boundingBoxVec6)}</span>
        </div>
      ),
    },
    {
      title: "Experience",
      dataIndex: "neededExperience",
      key: "neededExperience",
      sorter: localeCompareBy<APITask>((task) => task.neededExperience.domain),
      width: 250,
      render: (neededExperience: APITask["neededExperience"]) =>
        neededExperience.domain !== "" || neededExperience.value > 0 ? (
          <Tag>
            {neededExperience.domain} : {neededExperience.value}
          </Tag>
        ) : null,
    },
    {
      title: "Creation Date",
      dataIndex: "created",
      key: "created",
      width: 200,
      sorter: compareBy<APITask>((task) => task.created),
      render: (created: APITask["created"]) => <FormattedDate timestamp={created} />,
      defaultSortOrder: "descend",
    },
    {
      title: "Action",
      key: "actions",
      width: 170,
      fixed: "right",
      render: (_unused, task: APITask) => (
        <>
          {task.status.finished > 0 ? (
            <div>
              <a
                href={`/annotations/CompoundTask/${task.id}`}
                title="Show Compound Annotation of All Finished Annotations"
              >
                <EyeOutlined className="icon-margin-right" />
                View Merged
              </a>
            </div>
          ) : null}
          <div>
            <a href={`/tasks/${task.id}/edit`} title="Edit Task">
              ,
              <EditOutlined className="icon-margin-right" />
              Edit
            </a>
          </div>
          {task.status.pending > 0 ? (
            <div>
              <LinkButton onClick={partial(assignTaskToUser, task)} icon={<UserAddOutlined />}>
                Manually Assign to User
              </LinkButton>
            </div>
          ) : null}
          {task.status.finished > 0 ? (
            <div>
              <AsyncLink
                onClick={() => {
                  const includesVolumeData = task.type.tracingType !== "skeleton";
                  return downloadAnnotationAPI(task.id, "CompoundTask", includesVolumeData);
                }}
                title="Download All Finished Annotations"
                icon={<DownloadOutlined className="icon-margin-right" />}
              >
                Download
              </AsyncLink>
            </div>
          ) : null}
          <div>
            <LinkButton onClick={partial(deleteTask, task)} icon={<DeleteOutlined />}>
              Delete
            </LinkButton>
          </div>
        </>
      ),
    },
  ];

  return (
    <AdminPage
      title="Tasks"
      descriptionURI="https://docs.webknossos.org/webknossos/tasks_projects/tasks.html"
      description="Search, inspect, and manage task instances across projects and datasets."
      actions={
        <Link to="/tasks/create">
          <Button icon={<PlusOutlined />} type="primary">
            Add Task
          </Button>
        </Link>
      }
      search={<Search allowClear onChange={handleSearch} value={searchQuery} />}
      alerts={features().isWkorgInstance ? <AnnotationServicesAd /> : null}
      filters={
        <TaskSearchForm
          onChange={async (queryObject) => {
            await fetchData(queryObject);
          }}
          initialFieldValues={initialFieldValues}
          isLoading={isLoading}
          onDownloadAllTasks={downloadSettingsFromAllTasks}
        />
      }
    >
      <Spin spinning={isLoading} size="large">
        <FixedExpandableTable
          dataSource={getFilteredTasks()}
          rowKey="id"
          columns={columns}
          pagination={{
            defaultPageSize: 50,
            onChange: scrollToTop,
          }}
          expandable={{
            expandedRowRender: (task) => <TaskAnnotationView task={task} />,
          }}
          locale={{
            emptyText: renderPlaceholder(),
          }}
        />

        {getAnonymousTaskLinkModal()}
      </Spin>
    </AdminPage>
  );
}

export default TaskListView;
